import { effectSchemaToZod } from "@redotech/effect-api-shared-common/zod";
import { CurrentProductInfoSchema } from "@redotech/redo-support-common/concierge-conversation/rpc/concierge-conversation-schema";
import * as z from "zod";
import { AiActivityNotification } from "../ai-activity-notification";

/**
 * Zod schemas for widget conversation stream wire format.
 *
 * Lives in `effect-api-shared` (not `effect-api-stream-schemas`) so the
 * stream channel schemas can reference Effect-domain unions (e.g.
 * `AiActivityNotification`) without forcing `effect-api-shared` to depend
 * on `effect-api-stream-schemas`. The dependency direction is:
 * `effect-api-stream-schemas` → `effect-api-shared`.
 */

export const widgetMessageSchema = z.object({
  _id: z.string(),
  content: z.string(),
  contentType: z.string().nullable(),
  type: z.string(),
  user: z.string().nullable(),
  sentAt: z.coerce.date(),
  createdAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date().nullable(),
  visibility: z.string().nullable(),
  toolCallId: z.string().nullable(),
  pendingToolCallId: z.string().nullable(),
  draftInfo: z.unknown().nullable(),
  structuredContent: z.unknown().nullable(),
  confirmationStateInformation: z.unknown().nullable(),
  files: z
    .array(
      z.object({
        url: z.string(),
        description: z.string().nullable(),
        mimeType: z.string().nullable(),
        createdAt: z.coerce.date(),
      }),
    )
    .nullable(),
});
export type WidgetMessage = z.infer<typeof widgetMessageSchema>;

export const widgetConversationResponseSchema = z.object({
  _id: z.string(),
  team: z.string(),
  assignee: z.string().nullable(),
  messages: z.array(widgetMessageSchema),
  platform: z.string(),
  status: z.string(),
  lastResponseAt: z.coerce.date(),
  lastCustomerResponseAt: z.coerce.date().nullable(),
  lastKnownStepId: z.number().nullable(),
  inputStepType: z.string().nullable(),
  fourDigitCodeId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type WidgetConversationResponse = z.infer<
  typeof widgetConversationResponseSchema
>;

export const widgetConversationListItemSchema = z.object({
  _id: z.string(),
  recentMessageText: z.string().nullable(),
  lastMessageCreatedAt: z.coerce.date().nullable(),
  assignee: z.string().nullable(),
  platform: z.string(),
});
export type WidgetConversationListItem = z.infer<
  typeof widgetConversationListItemSchema
>;

// ---------------------------------------------------------------------------
// Stream channel schemas
// ---------------------------------------------------------------------------

export const widgetConversationUpdateEventSchema = z.object({
  _tag: z.literal("conversationUpdate"),
  conversation: widgetConversationResponseSchema,
});
export type WidgetConversationUpdateEvent = z.infer<
  typeof widgetConversationUpdateEventSchema
>;

export const widgetTypingEventSchema = z.object({
  _tag: z.literal("typing"),
  conversationId: z.string(),
  userId: z.string(),
});
export type WidgetTypingEvent = z.infer<typeof widgetTypingEventSchema>;

export const widgetTypingClearedEventSchema = z.object({
  _tag: z.literal("typingCleared"),
  conversationId: z.string(),
  userId: z.string(),
});
export type WidgetTypingClearedEvent = z.infer<
  typeof widgetTypingClearedEventSchema
>;

/**
 * Event union for the `widget-conversations` channel. Carries either a
 * full conversation refresh (`conversationUpdate`) or a streaming AI
 * activity event (text delta / status / tool call / reasoning / message
 * added) fanned out from `ReplyAgentExecutionService.generateDraft`.
 *
 * AI event shapes are reused directly from the Effect Schema definition
 * via `effectSchemaToZod` — no Zod duplication.
 */
export const widgetConversationsEventSchema = z.union([
  widgetConversationUpdateEventSchema,
  widgetTypingEventSchema,
  widgetTypingClearedEventSchema,
  effectSchemaToZod(AiActivityNotification),
]);
export type WidgetConversationsEvent = z.infer<
  typeof widgetConversationsEventSchema
>;

const conversationIdSchema = z
  .string()
  .regex(/^cnv_[0-9A-Za-z]+$/, "Topic must match cnv_<base62>");

export const widgetConversationsStreamInputSchema = z.object({
  conversationId: conversationIdSchema,
});
export type WidgetConversationsStreamInput = z.infer<
  typeof widgetConversationsStreamInputSchema
>;

export const widgetConversationsStreamInitialSchema = z.object({
  _tag: z.literal("widgetConversationsInitial"),
  conversation: widgetConversationResponseSchema,
});
export type WidgetConversationsStreamInitial = z.infer<
  typeof widgetConversationsStreamInitialSchema
>;

// ---------------------------------------------------------------------------
// Write endpoint payload schemas
// ---------------------------------------------------------------------------

export const widgetQuickResponseSchema = z.object({
  text: z.string(),
  shouldSend: z.boolean(),
});
export type WidgetQuickResponse = z.infer<typeof widgetQuickResponseSchema>;

export const widgetInitialMessageSchema = z.object({
  type: z.string(),
  content: z.string(),
});
export type WidgetInitialMessage = z.infer<typeof widgetInitialMessageSchema>;

/** Payload for creating a new widget conversation (first message). */
export const createWidgetConversationPayloadSchema = z.object({
  message: z.string(),
  customerTimezone: z.string().optional(),
  activeCartId: z.string().optional(),
  initialMessages: z.array(widgetInitialMessageSchema).optional(),
  currentStepId: z.number().nullable().optional(),
  emailCollected: z.boolean().optional(),
  fourDigitCodeId: z.string().optional(),
  generateQuickResponses: z.boolean().optional(),
  // The PDP the customer had open when they sent this message, if any.
  currentProductInfo: CurrentProductInfoSchema.optional(),
});
export type CreateWidgetConversationPayload = z.infer<
  typeof createWidgetConversationPayloadSchema
>;

/** Payload for sending a message to an existing widget conversation. */
export const sendWidgetMessagePayloadSchema = z.object({
  message: z.string(),
  customerTimezone: z.string().optional(),
  activeCartId: z.string().optional(),
  currentStepId: z.number().nullable().optional(),
  emailCollected: z.boolean().optional(),
  fourDigitCodeId: z.string().optional(),
  generateQuickResponses: z.boolean().optional(),
  // The PDP the customer had open when they sent this message, if any.
  currentProductInfo: CurrentProductInfoSchema.optional(),
});
export type SendWidgetMessagePayload = z.infer<
  typeof sendWidgetMessagePayloadSchema
>;

/** Payload for converting an AI conversation to a support conversation. */
export const convertWidgetConversationToSupportPayloadSchema = z.object({
  email: z.string().optional(),
  handoffType: z.enum(["live_chat", "email"]).optional(),
  contactSupportMessageId: z.string().optional(),
});
export type ConvertWidgetConversationToSupportPayload = z.infer<
  typeof convertWidgetConversationToSupportPayloadSchema
>;

/** Payload for handing a chat that has no server conversation yet to support. The server picks the handoff type. */
export const convertNewWidgetConversationToSupportPayloadSchema = z.object({
  email: z.string().optional(),
  initialMessages: z.array(widgetInitialMessageSchema),
});
export type ConvertNewWidgetConversationToSupportPayload = z.infer<
  typeof convertNewWidgetConversationToSupportPayloadSchema
>;

/** Payload for associating anonymous conversations with a logged-in customer. */
export const associateWidgetConversationsWithCustomerPayloadSchema = z.object({
  conversationIds: z.array(z.string()),
});
export type AssociateWidgetConversationsWithCustomerPayload = z.infer<
  typeof associateWidgetConversationsWithCustomerPayloadSchema
>;

/** Payload for tracking a concierge interaction. */
export const widgetConciergeInteractedPayloadSchema = z.object({
  shopifySessionId: z.string(),
  conversationId: z.string(),
});
export type WidgetConciergeInteractedPayload = z.infer<
  typeof widgetConciergeInteractedPayloadSchema
>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const widgetMessageSendResponseSchema = z.object({
  conversation: widgetConversationResponseSchema,
  conversationToken: z.string().nullable(),
  quickResponses: z.array(widgetQuickResponseSchema).optional(),
});
export type WidgetMessageSendResponse = z.infer<
  typeof widgetMessageSendResponseSchema
>;

export const widgetOkResponseSchema = z.object({ ok: z.literal(true) });
export type WidgetOkResponse = z.infer<typeof widgetOkResponseSchema>;

// ---------------------------------------------------------------------------
// Event constructors
// ---------------------------------------------------------------------------

export const WidgetEvent = {
  conversationUpdate: (
    conversation: WidgetConversationResponse,
  ): WidgetConversationUpdateEvent => ({
    _tag: "conversationUpdate",
    conversation,
  }),

  typing: (conversationId: string, userId: string): WidgetTypingEvent => ({
    _tag: "typing",
    conversationId,
    userId,
  }),

  typingCleared: (
    conversationId: string,
    userId: string,
  ): WidgetTypingClearedEvent => ({
    _tag: "typingCleared",
    conversationId,
    userId,
  }),
} as const;
